import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Lock, User, Loader, ChefHat, Crown } from 'lucide-react';
import toast from 'react-hot-toast';

const API_BASE = process.env.REACT_APP_API_BASE_URL;

const LoginPage = () => {
  const navigate = useNavigate();
  const [tab, setTab] = useState('staff'); // 'owner' | 'staff'

  // Owner form
  const [ownerEmail, setOwnerEmail] = useState('');
  const [ownerPassword, setOwnerPassword] = useState('');

  // Staff form
  const [employeeId, setEmployeeId] = useState('');
  const [pinCode, setPinCode] = useState('');

  const [loading, setLoading] = useState(false);

  // ── Save session to localStorage (shared shape for both owner & staff) ──
  const saveSession = (data) => {
    localStorage.setItem('staffToken',   data.token);
    localStorage.setItem('staffId',      String(data.staffId   || ''));
    localStorage.setItem('ownerId',      String(data.ownerId   || ''));
    localStorage.setItem('role',         data.role             || 'STAFF');
    localStorage.setItem('isOwner',      String(data.isOwner   || false));
    localStorage.setItem('staffName',    data.staffName || data.ownerName || '');
    localStorage.setItem('restaurantId', String(data.restaurantId || ''));
    localStorage.setItem('restaurants',  JSON.stringify(data.restaurants || []));
    localStorage.setItem('permissions',  JSON.stringify(data.permissions || []));
  };

  // ── Owner Login ────────────────────────────────────────────────────────
  const handleOwnerLogin = async (e) => {
    e.preventDefault();
    if (!ownerEmail.trim() || !ownerPassword.trim()) {
      toast.error('Please enter email and password');
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/billing-pos/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: ownerEmail.trim(), password: ownerPassword.trim() })
      });
      const data = await res.json();
      console.log('[Owner Login] Response:', data);

      if (res.ok && data.token) {
        saveSession(data);
        toast.success(`Welcome, ${data.staffName || data.ownerName || 'Owner'}!`);
        // Owner always goes to restaurant selection (may have multiple)
        navigate('/select-restaurant');
      } else {
        toast.error(data.error || data.message || 'Login failed');
      }
    } catch (err) {
      console.error('Owner login error:', err);
      toast.error('Cannot connect to server. Please check your connection.', { duration: 5000 });
    } finally {
      setLoading(false);
    }
  };

  // ── Staff Login ────────────────────────────────────────────────────────
  const handleStaffLogin = async (e) => {
    e.preventDefault();
    if (!employeeId.trim() || !pinCode.trim()) {
      toast.error('Please enter Employee ID and PIN');
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/billing-pos/staff-auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ employeeId: employeeId.trim(), pinCode: pinCode.trim() })
      });
      const data = await res.json();
      console.log('[Staff Login] Response:', data);

      if (res.ok && data.token) {
        // ── Permission gate: staff must have CREATE_ORDER to use staff ordering ──
        const perms = data.permissions || [];
        if (!data.isOwner && !perms.includes('CREATE_ORDER')) {
          toast.error('Staff Order permission is disabled. Contact your manager.', { duration: 5000 });
          setLoading(false);
          return;
        }
        saveSession(data);
        toast.success(`Welcome, ${data.staffName || 'Staff'}!`);
        // Staff login returns a single restaurantId → skip restaurant selection
        navigate('/tables');
      } else {
        const msg = data.error || data.message || 'Login failed';
        if (res.status === 401) {
          toast.error(msg.toLowerCase().includes('not active')
            ? 'Your account is not active. Contact your manager.'
            : msg);
        } else if (res.status === 403) {
          toast.error('POS subscription not active for this restaurant.');
        } else {
          toast.error(msg);
        }
      }
    } catch (err) {
      console.error('Staff login error:', err);
      if (err.message === 'Failed to fetch' || err.name === 'TypeError') {
        toast.error('Cannot connect to server. Please check if backend is running.', { duration: 5000 });
      } else {
        toast.error('Login failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-red-50 flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        {/* Logo/Header */}
        <div className="text-center mb-8">
          <div className="w-20 h-20 bg-gradient-to-br from-orange-500 to-red-600 rounded-3xl mx-auto mb-4 flex items-center justify-center shadow-lg">
            <ChefHat className="w-10 h-10 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-gray-800 mb-2">Staff Order System</h1>
          <p className="text-gray-600">Sign in to continue</p>
        </div>

        {/* Card */}
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
          {/* Tabs */}
          <div className="flex">
            <button
              type="button"
              onClick={() => setTab('staff')}
              className={`flex-1 py-3 text-sm font-semibold flex items-center justify-center gap-2 transition-colors ${
                tab === 'staff'
                  ? 'bg-orange-500 text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              <User size={16} /> Staff Login
            </button>
            <button
              type="button"
              onClick={() => setTab('owner')}
              className={`flex-1 py-3 text-sm font-semibold flex items-center justify-center gap-2 transition-colors ${
                tab === 'owner'
                  ? 'bg-orange-500 text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              <Crown size={16} /> Owner Login
            </button>
          </div>

          <div className="p-8">
            {/* ── Staff Login Form ── */}
            {tab === 'staff' && (
              <form onSubmit={handleStaffLogin} className="space-y-5">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Employee ID
                  </label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
                    <input
                      type="text"
                      placeholder="Enter your Employee ID"
                      value={employeeId}
                      onChange={(e) => setEmployeeId(e.target.value)}
                      className="w-full pl-11 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                      disabled={loading}
                      autoComplete="username"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    PIN
                  </label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
                    <input
                      type="password"
                      placeholder="Enter your 4-digit PIN"
                      value={pinCode}
                      onChange={(e) => setPinCode(e.target.value)}
                      className="w-full pl-11 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                      disabled={loading}
                      autoComplete="current-password"
                      inputMode="numeric"
                      maxLength={6}
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-gradient-to-r from-orange-500 to-red-600 text-white py-3 rounded-lg font-semibold hover:from-orange-600 hover:to-red-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {loading ? <><Loader className="w-5 h-5 animate-spin" /> Signing in...</> : 'Staff Login'}
                </button>
              </form>
            )}

            {/* ── Owner Login Form ── */}
            {tab === 'owner' && (
              <form onSubmit={handleOwnerLogin} className="space-y-5">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Owner Email
                  </label>
                  <div className="relative">
                    <Crown className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
                    <input
                      type="email"
                      placeholder="Enter your owner email"
                      value={ownerEmail}
                      onChange={(e) => setOwnerEmail(e.target.value)}
                      className="w-full pl-11 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                      disabled={loading}
                      autoComplete="email"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Password
                  </label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
                    <input
                      type="password"
                      placeholder="Enter your password"
                      value={ownerPassword}
                      onChange={(e) => setOwnerPassword(e.target.value)}
                      className="w-full pl-11 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                      disabled={loading}
                      autoComplete="current-password"
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-gradient-to-r from-orange-500 to-red-600 text-white py-3 rounded-lg font-semibold hover:from-orange-600 hover:to-red-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {loading ? <><Loader className="w-5 h-5 animate-spin" /> Signing in...</> : 'Owner Login'}
                </button>
              </form>
            )}
          </div>
        </div>

        {/* Footer */}
        <p className="text-center text-sm text-gray-600 mt-6">
          Trivana Hospitality &copy; 2026
        </p>
      </div>
    </div>
  );
};

export default LoginPage;
